import axios from 'axios';
import { forEach, get, set, toNumber } from 'lodash';
import { flow, map, fromPairs } from 'lodash/fp';

import {
  GIFT_CART_RECIPIENT_EMAIL_ATTRIBUTE,
  GIFT_CART_RECIPIENT_NAME_ATTRIBUTE,
  GIFT_CART_SEND_ON_ATTRIBUTE,
  GIFT_CART_MODEL_PROPERTY_IS_DGC_WITH_RECIPIENT_EMAIL,
  GIFT_CART_MODEL_PROPERTY_DGC_EMAIL,
  GIFT_CART_MODEL_PROPERTY_DGC_NAME,
  GIFT_CART_MODEL_PROPERTY_DGC_SEND_ON,
} from 'constants/digitalGiftCard';
import DigitalGiftCardUtils from 'utils/DigitalGiftCard';
import Log from 'utils/Log';
import Timestamp from 'utils/Timestamp';

import normalizeCart from './schema';

// Necessary to trigger out of stock errors (https://ecommerce.shopify.com/c/shopify-apis-and-technology/t/ajax-api-only-sends-error-with-jquery-391381)
const axiosParams = {
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
};

class AjaxCart {
  static formatLineItems = unformattedItems => {
    const orderedIds = [];

    const itemMap = unformattedItems.reduce((formattedItems, nextItem) => {
      const addItem = item => {
        orderedIds.push(item.id);
        // eslint-disable-next-line no-param-reassign
        formattedItems[item.id] = item;
      };

      addItem({
        lineItemType: 'VARIANT',
        ...nextItem,
      });

      return formattedItems;
    }, {});

    // Put items back in the original order
    const orderedItemList = orderedIds.map(id => itemMap[id]);

    return orderedItemList;
  };

  static getCartCount = cartItems =>
    cartItems.reduce((quantity, item) => {
      // eslint-disable-next-line no-param-reassign
      quantity += item?.quantity ?? 1;
      return quantity;
    }, 0);

  /**
   * The response from Cart AJAX API provides 2 different objets { data, cart } both are processed and merged
   *   into one object that becomes the cart model object
   *
   * Digital gift cards with recipient info share the same item ID, there is no way to relate the DGC
   *   item on the 'cart' object with the DGC item on the 'data' object, the solution was to mutate the existing objects
   *
   * Replaces the ID of those items that are DGC and contains recipient info with a new ID that includes the recipient email
   *   id generated example '1234_dgc_344939393'
   *
   * Finally added the recipient information on the 'data' object to later be used on the UI, this is to be compatible
   *   with the Storefront Cart models currently used
   */
  static _updateCartItemsWithDigitalGiftCardIds = (cart, data) => {
    cart?.items?.forEach(cartItem => {
      const recipientEmail = get(cartItem.properties, GIFT_CART_RECIPIENT_EMAIL_ATTRIBUTE);

      // If the item contains a recipient email, then is a Digital Gift Card with recipient info
      if (recipientEmail) {
        const currentId = cartItem.id;
        const generatedDgcId = DigitalGiftCardUtils.generateDigitalGiftCardId({
          id: currentId,
          email: recipientEmail,
        });

        // Replace the original id on 'cart' object with the DGC generated
        set(cartItem, 'id', generatedDgcId);

        // Replace the original id on 'data' object with the DGC generated
        const dataItem = data?.items?.find(dataItem => dataItem.id === currentId);
        set(dataItem, 'id', generatedDgcId);

        // Add recipient information on the data object to match with StorefrontCart model
        const recipientName = get(cartItem.properties, GIFT_CART_RECIPIENT_NAME_ATTRIBUTE);
        const recipientSendOn = get(cartItem.properties, GIFT_CART_SEND_ON_ATTRIBUTE);

        set(dataItem, GIFT_CART_MODEL_PROPERTY_IS_DGC_WITH_RECIPIENT_EMAIL, true);
        set(dataItem, GIFT_CART_MODEL_PROPERTY_DGC_EMAIL, recipientEmail);
        set(dataItem, GIFT_CART_MODEL_PROPERTY_DGC_NAME, recipientName);
        set(dataItem, GIFT_CART_MODEL_PROPERTY_DGC_SEND_ON, recipientSendOn);
      }
    });
  };

  // The "buster" param busts the cache to prevent an IE bug where GETs get cached
  static getCartJson = async () => {
    const cart = await axios
      .get(`/cart?view=react-json&buster=${Timestamp.generate()}`)
      .then(({ data }) => data);
    return cart;
  };

  /**
   * This endpoint returns two incomplete sets of cart data (cart, and data) and has to merge them together to get the full payload
   */
  static fetchCart = async () => {
    const { cart, data } = await AjaxCart.getCartJson();

    // Update DGC items with recipient info
    AjaxCart._updateCartItemsWithDigitalGiftCardIds(cart, data);

    // Make a map of cart items
    const cartItemMap = flow(
      map(item => [item.id, item]),
      fromPairs
    )(cart?.items);

    // Merge keys (variant_options, product_title) from cart items onto data items
    forEach(data?.items, item => {
      const cartItem = get(cartItemMap, item.id);

      const variant_options = get(cartItem, 'variant_options');
      const product_title = get(cartItem, 'product_title');
      // grab the properties that are in [item, key] format which puts back as object
      const properties = fromPairs(get(item, 'properties'));

      if (variant_options) {
        set(item, 'variant_options', variant_options);
      }

      if (product_title) {
        set(item, 'product_title', product_title);
      }

      if (properties) {
        set(item, 'properties', properties);
      }
    });

    data.items = AjaxCart.formatLineItems(data.items);

    data.item_count = AjaxCart.getCartCount(data.items);

    // Gift message gets weird escaped characters (eg `'` `"` `<` `>`) on data but not cart, so we port them from one to the other.
    const wellFormattedGiftMessage = get(cart, ['attributes', 'gift_message']);
    if (wellFormattedGiftMessage) {
      set(data, ['attributes', 'gift_message'], wellFormattedGiftMessage);
    }

    const normalizedCart = normalizeCart(data);
    return normalizedCart;
  };

  static addLineItems = async ({ items, properties }) => {
    const results = [];
    const errors = [];

    // Ajax cart does not support simultaneous add-to-carts, which is why they are processed sequentially instead of in parallel
    for (const id of Object.keys(items)) {
      try {
        const quantity = items[id];
        const res = await AjaxCart._addLineItem(toNumber(id), quantity, properties);

        results.push(res);
      } catch (error) {
        const { isOutOfStock, errorMessage, message, description } =
          AjaxCart._decodeAddToCartError(error);

        if (!isOutOfStock) {
          Log.warn(description);
          Log.error(message, 'Adding line item failed.');
        }

        errors.push(errorMessage);
        throw new Error(`Ajax:addLineItems - failed: ${JSON.stringify(errors)}`);
      }
    }

    const cart = await AjaxCart.fetchCart();

    return { cart, errors };
  };

  static updateLineItem = async (id, quantity) => {
    const errors = [];
    try {
      await AjaxCart._updateLineItem(id, quantity);
    } catch (error) {
      errors.push(new Error(`AjaxCart:updateLineItem - could not post, ${error}`));
    }

    const cart = await AjaxCart.fetchCart();

    return { cart, errors };
  };

  static removeLineItem = async id => {
    await AjaxCart.updateLineItem(id, 0);
    const cart = await AjaxCart.fetchCart();
    return { cart };
  };

  static setAttribute = async (attributeName, value) =>
    axios.post(
      '/cart/update.js',
      `attributes[${attributeName}]=${encodeURIComponent(value || '')}`,
      axiosParams
    );

  /* Receive an object where the properties and values will become attributes and values in the cart
   *
   * Example:
   * {
   *  _isLoggedInToShopRunner: false,
   *  _srToken: 'unset',
   * }
   *
   * API Doc: https://shopify.dev/docs/api/ajax/reference/cart#update-cart-attributes
   */
  static setAttributes = async newAttributes => {
    const updateAttributesPayload = {
      attributes: newAttributes,
    };

    return axios.post('/cart/update.js', updateAttributesPayload, axiosParams);
  };

  static clearCart = async () => axios.post('/cart/clear.js', {}, axiosParams);

  static _addLineItem = async (id, quantity = 1, properties) => {
    const promise = new Promise((resolve, reject) => {
      if (!id) {
        reject(new Error('Adding line item requires an id.'));
      }

      return axios
        .post(
          '/cart/add.js',
          {
            id,
            quantity,
            properties,
          },
          axiosParams
        )
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
    const result = await promise;
    return result;
  };

  static _updateLineItem = async (id, quantity) => {
    const promise = new Promise((resolve, reject) => {
      if (!id) {
        reject(new Error('Updating line item requires an id.'));
      }

      if (quantity !== 0 && !quantity) {
        reject(new Error('Updating line item requires a quantity.'));
      }

      let rawId = id;
      if (DigitalGiftCardUtils.isDigitalGiftCardId(id)) {
        rawId = DigitalGiftCardUtils.getIdFromDigitalGiftCardId(id);
      }

      return axios
        .post(
          '/cart/update.js',
          {
            updates: { [rawId]: quantity },
          },
          axiosParams
        )
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
    const result = await promise;
    return result;
  };

  // Generalized method for making sense of add to cart errors
  static _decodeAddToCartError = error => {
    const defaultErrorMessage = 'Something went wrong.';

    const responseData = get(error, ['response', 'data']);
    const status = get(responseData, ['status']);
    const description = get(responseData, ['description']);
    const message = get(responseData, ['message']);

    // In most scenarios, error messages from the AJAX API are cryptic and not useful for the end user
    // When out of stock, we do want to present the error from the API
    // In all other cases, we show a general useer-friendly message
    const isOutOfStock = status === 422;
    const errorMessage = isOutOfStock ? description : defaultErrorMessage;

    return { isOutOfStock, errorMessage, message, description };
  };
}

export default AjaxCart;
